// Verwaltung aller Alerts: Editorial v4 — 2-Spalten-Layout auf Desktop.
// Links: Liste mit Toggle-Switch + rundem X. Rechts: Inline-Create-Card mit
// ALLEN Alert-Typen (Bedingung-Dropdown: Preis/%/Haltefrist/Positions-Wert/
// Portfolio-Wert; Premium-Typen öffnen bei Free-Usern die Pricing-Seite).
// Coin-Auswahl via zentriertem Such-Overlay (CoinSearchOverlay) über die volle
// COIN_LIST — echte Coin-Icons via CoinImage (CDN-Fallback per Symbol).

import { useEffect, useMemo, useRef, useState } from 'react';
import { F, MONO_FEAT, makeAccent, mr } from '../styles/theme';
import { useTheme } from '../context/ThemeContext';
import { listAlerts, updateAlert, deleteAlert, createAlert, ApiError } from '../services/alertsApi';
import {
  getPermissionState, isPushSupported, disablePush,
  isStandalonePWA, isIOSSafari, enablePush, ensurePushRegistered,
} from '../services/pushClient';
import AlertWizard from '../components/AlertWizard';
import InfoBanner from '../components/InfoBanner';
import CoinImage from '../components/CoinImage';
import Private from '../components/Private';
import { Icon, Icons } from '../components/red/Icons';
import TierBadge from '../components/red/TierBadge';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { COIN_LIST, COIN_MAP, fetchMarketData } from '../utils/coingecko';
import { calcPortfolio } from '../utils/portfolioCalc';
import { useUserPlan } from '../context/UserPlanContext';
import { useT } from '../i18n/useT';
import { useCurrency } from '../context/CurrencyContext';
import { fmt } from '../utils/format';

// CoinGecko-ID (z.B. 'bitcoin') → Ticker-Symbol ('BTC'). COIN_MAP ist nach
// Symbol indiziert, daher hier ein einmalig aufgebauter ID→Symbol-Index.
const ID_TO_SYM = COIN_LIST.reduce((m, c) => { if (c.id) m[c.id.toLowerCase()] = c.sym; return m; }, {});

function describeAlert(a, t) {
  // coinSym ist das Ticker-Symbol (BTC); coinId die CoinGecko-ID (bitcoin).
  // Symbol bevorzugen; alte Alerts ohne coinSym über ID→Symbol-Index auflösen,
  // sonst zeigt das Label "BITCOIN" statt "BTC".
  const sym = a.coinSym || ID_TO_SYM[String(a.coinId || '').toLowerCase()];
  const coin = String(sym || a.coinId || '').toUpperCase();
  const unit = a.type === 'pct_change' ? '%' : (a.currency || 'EUR');
  const th = (a.threshold != null) ? a.threshold : '';
  if (a.type === 'price_above') return { sym: coin, line: t('alert_line_rises_above', { coin }), value: `${th} ${unit}` };
  if (a.type === 'price_below') return { sym: coin, line: t('alert_line_falls_below', { coin }), value: `${th} ${unit}` };
  if (a.type === 'pct_change')  return { sym: coin, line: t('alert_line_moves', { coin }), value: `± ${th} %` };
  if (a.type === 'holding_period')  return { sym: coin, line: t('alert_line_holding_period', { coin }), value: '' };
  if (a.type === 'position_value')  return { sym: coin, line: t('alert_line_position_at', { coin }), value: `${th} ${unit}`, sensitive: true };
  if (a.type === 'portfolio_value') return { sym: '∑', line: t('alert_line_portfolio_at'), value: `${th} ${unit}`, sensitive: true };
  return { sym: coin, line: a.type, value: '' };
}

function fmtDate(iso, locale = 'de-DE') {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return '—'; }
}

// ── Holdings-Symbole aus txs ableiten (positiver Bestand, non-fiat) ───────────
// Quelle = kanonische Engine (calcPortfolio → buildFifoPool). KEINE eigene
// Saldo-Rechnung mehr: die alte Variante las englische Felder (tx.amount/tx.type)
// und ignorierte Swaps → lieferte für die meisten User nur den BTC/ETH/SOL-
// Fallback. calcPortfolio enthält genau die Coins mit Bestand > 0 (fiat-bereinigt).
function deriveHoldings(txs) {
  const list = Object.keys(calcPortfolio(txs || [])).sort();
  // Fallback-Defaults wenn Portfolio leer
  if (list.length === 0) return ['BTC', 'ETH', 'SOL'];
  // Stelle sicher dass BTC/ETH immer verfügbar
  for (const must of ['BTC', 'ETH']) if (!list.includes(must)) list.push(must);
  return list;
}

// ── Editorial Toggle-Switch (44×26, weißer Daumen) ──────────────────────────
function ToggleSwitch({ active, onClick, accent, T }) {
  const { t } = useT();
  return (
    <button
      onClick={onClick}
      title={active ? t('alert_pause') : t('alert_activate')}
      aria-pressed={active}
      style={{
        width: 44, height: 26, borderRadius: 999,
        background: active ? accent.signal : T.surfaceHi,
        border: `1px solid ${active ? accent.signal : T.line}`,
        cursor: 'pointer', position: 'relative',
        transition: 'background .15s, border-color .15s',
        flexShrink: 0,
      }}
    >
      <span style={{
        position: 'absolute', top: 2,
        left: active ? 20 : 2,
        width: 20, height: 20, borderRadius: '50%',
        background: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
        transition: 'left .15s',
      }} />
    </button>
  );
}

// ── Editorial AlertRow ──────────────────────────────────────────────────────
function AlertRow({ a, marketData, onToggle, onDelete, T, accent }) {
  const { t } = useT();
  const { locale } = useCurrency();
  const [hover, setHover] = useState(false);
  const [delHover, setDelHover] = useState(false);
  const d = describeAlert(a, t);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '15px 18px', borderRadius: mr(16),
        background: hover ? T.surfaceHi : T.surface,
        border: `1px solid ${T.line}`,
        opacity: a.active === false ? 0.55 : 1,
        transition: 'background .14s, opacity .14s',
      }}
    >
      {d.sym === '∑'
        ? <div style={{
            width: 40, height: 40, borderRadius: '50%',
            border: `1px solid ${T.line}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: T.sub, fontFamily: F.display, fontSize: 18, fontWeight: 800,
          }}>∑</div>
        : <CoinImage sym={d.sym} marketData={marketData} size={40} />}

      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{
          fontFamily: F.family, fontSize: 14, fontWeight: 700,
          color: T.text, letterSpacing: '-0.01em',
        }}>
          {d.line}{d.value && (
            <span style={{ ...MONO_FEAT, marginLeft: 6 }}>
              {d.sensitive ? <Private>{d.value}</Private> : d.value}
            </span>
          )}
        </div>
        <div style={{ fontSize: 11.5, color: T.mute, fontFamily: F.family, marginTop: 2 }}>
          {a.recurring === false ? t('alert_once') : t('alert_recurring')}
          {a.lastTriggeredAt ? ` · ${t('alert_last_triggered', { date: fmtDate(a.lastTriggeredAt, locale) })}` : a.createdAt ? ` · ${t('alert_created_at', { date: fmtDate(a.createdAt, locale) })}` : ''}
        </div>
      </div>

      <ToggleSwitch active={a.active !== false} onClick={() => onToggle(a)} accent={accent} T={T} />

      <button
        onClick={() => onDelete(a)}
        aria-label={t('alert_delete_aria')}
        onMouseEnter={() => setDelHover(true)}
        onMouseLeave={() => setDelHover(false)}
        style={{
          width: 30, height: 30, borderRadius: 999,
          background: 'transparent',
          border: `1px solid ${delHover ? accent.signal : T.line}`,
          cursor: 'pointer', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
          transition: 'border-color .15s',
        }}
      >
        <Icon d={Icons.close} size={14} stroke={delHover ? accent.signal : T.sub} sw={2.4} />
      </button>
    </div>
  );
}

// ── Coin-Such-Overlay (zentriert, Rest faded) ────────────────────────────────
function CoinSearchOverlay({ open, onClose, onSelect, marketData, T }) {
  const { t } = useT();
  const { cgParam } = useCurrency();
  const [q, setQ] = useState('');
  // Echte Coin-Icons für die angezeigten Listen-Coins nachladen: die per Prop
  // gereichte marketData deckt nur die besessenen Coins ab, daher hätten die
  // ~80 Such-Coins sonst nur das CDN-Fallback (das neuere Coins nicht kennt) →
  // 3-Buchstaben-Kreis. Wir holen die CoinGecko-`image`-URLs (zuverlässig +
  // CSP-konform via /api/coin-markets) und mergen sie über die Prop-Daten.
  const [iconData, setIconData] = useState({});
  const fetchedRef = useRef(null);

  useEffect(() => { if (open) setQ(''); }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return COIN_LIST.slice(0, 80);
    const starts = [], contains = [];
    for (const c of COIN_LIST) {
      const sym = c.sym.toLowerCase();
      const name = (c.name || '').toLowerCase();
      if (sym === s || sym.startsWith(s) || name.startsWith(s)) starts.push(c);
      else if (sym.includes(s) || name.includes(s)) contains.push(c);
    }
    return [...starts, ...contains].slice(0, 80);
  }, [q]);

  // Fehlende Icons für die aktuell angezeigten Coins nachladen (dedupe via Set,
  // damit Tippen/Re-Open nicht mehrfach feuert). marketData ist nach CoinGecko-
  // ID indiziert — c.id == coinMeta.id, daher greift CoinImage den Treffer.
  useEffect(() => {
    if (!open) return;
    if (!fetchedRef.current) fetchedRef.current = new Set();
    const seen = fetchedRef.current;
    const ids = [];
    for (const c of results) {
      if (c.id && !marketData?.[c.id] && !seen.has(c.id)) { seen.add(c.id); ids.push(c.id); }
    }
    if (!ids.length) return;
    let cancelled = false;
    fetchMarketData(ids, cgParam).then(data => {
      if (!cancelled && data) setIconData(prev => ({ ...prev, ...data }));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [open, results, marketData, cgParam]);

  const mergedMarket = useMemo(() => ({ ...iconData, ...marketData }), [iconData, marketData]);

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      data-kb-overlay
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 'var(--kbpad)', zIndex: 400,
        background: 'rgba(0,0,0,0.78)',
        backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '12vh 16px 16px',
        transition: 'bottom var(--dur-kb) var(--ease-kb)',
        animation: 'redFadeIn 160ms ease',
      }}
    >
      <style>{`@keyframes redFadeIn{from{opacity:0}to{opacity:1}}`}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 460,
          background: T.surface, border: `1px solid ${T.line}`,
          borderRadius: mr(16), overflow: 'hidden',
          display: 'flex', flexDirection: 'column', maxHeight: '100%',
          boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
        }}
      >
        <div style={{ padding: 14, borderBottom: `1px solid ${T.line}` }}>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('alert_coin_search_placeholder')}
            style={{
              width: '100%', boxSizing: 'border-box',
              background: T.surfaceHi, border: `1px solid ${T.line}`,
              borderRadius: mr(16), padding: '12px 14px',
              color: T.text, fontFamily: F.family, fontSize: 15,
              fontWeight: 600, outline: 'none',
            }}
          />
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 6 }}>
          {results.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: T.sub, fontSize: 13, fontFamily: F.family }}>
              {t('alert_no_coin_found')}
            </div>
          ) : results.map((c) => (
            <button
              key={c.id}
              onClick={() => { onSelect(c); onClose(); }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                padding: '11px 12px', background: 'transparent', border: 'none',
                borderRadius: mr(16), cursor: 'pointer', textAlign: 'left',
                color: T.text,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = T.surfaceHi; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <CoinImage sym={c.sym} marketData={mergedMarket} size={30} />
              <span style={{ fontFamily: F.family, fontSize: 14, fontWeight: 700 }}>{c.sym}</span>
              <span style={{
                fontFamily: F.family, fontSize: 12.5, color: T.sub, fontWeight: 500,
                minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{c.name}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Create-Card (rechte Spalte / unten auf Mobile) ───────────────────────────
// ── ModernDropdown — custom Select (kein natives <select>) ────────────────────
// Button + Popover. Themed, abgerundet, Häkchen auf der Auswahl, Premium-Label.
// Schließt bei Außenklick/Escape. Tastatur: Enter/Space öffnet, Pfeile wählen.
function ModernDropdown({ value, options, onChange, T }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const current = options.find(o => o.id === value) || options[0];

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); window.removeEventListener('keydown', onKey); };
  }, [open]);

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          width: '100%', boxSizing: 'border-box',
          display: 'flex', alignItems: 'center', gap: 8,
          background: T.surfaceHi,
          border: `1px solid ${open ? T.text : T.line}`,
          borderRadius: mr(16), padding: '11px 12px',
          color: T.text, fontFamily: F.family, fontSize: 14, fontWeight: 700,
          cursor: 'pointer', textAlign: 'left', outline: 'none',
          transition: 'border-color .15s',
        }}
      >
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {current?.label}
        </span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
          stroke={T.sub} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
          style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div
          role="listbox"
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 50,
            background: T.surface, border: `1px solid ${T.line}`,
            borderRadius: mr(16), padding: 5,
            boxShadow: '0 16px 40px rgba(0,0,0,0.45)',
            maxHeight: 280, overflowY: 'auto',
            animation: 'redDropIn 140ms cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        >
          <style>{`@keyframes redDropIn{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}}`}</style>
          {options.map(o => {
            const selected = o.id === value;
            return (
              <button
                key={o.id}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => { onChange(o.id); setOpen(false); }}
                style={{
                  width: '100%', boxSizing: 'border-box',
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: selected ? T.surfaceHi : 'transparent',
                  border: 'none', borderRadius: mr(16),
                  padding: '10px 11px', cursor: 'pointer', textAlign: 'left',
                  color: T.text, fontFamily: F.family, fontSize: 13.5,
                  fontWeight: selected ? 700 : 600,
                }}
                onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = T.surfaceHi; }}
                onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ flex: 1, minWidth: 0 }}>{o.label}</span>
                {o.badge}
                {selected && (
                  <Icon d={Icons.check} size={14} stroke={T.text} sw={2.6} style={{ flexShrink: 0 }} />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const CONDITIONS = [
  { id: 'price_above',     labelKey: 'alert_cond_price_above',     tier: 'free' },
  { id: 'price_below',     labelKey: 'alert_cond_price_below',     tier: 'free' },
  { id: 'pct_change',      labelKey: 'alert_cond_pct_change',      tier: 'free' },
  { id: 'holding_period',  labelKey: 'alert_cond_holding_period',  tier: 'premium' },
  { id: 'position_value',  labelKey: 'alert_cond_position_value',  tier: 'premium' },
  { id: 'portfolio_value', labelKey: 'alert_cond_portfolio_value', tier: 'premium' },
];

function CreateCard({ holdings, marketData, onCreated, onOpenPricing, T, accent }) {
  const { t } = useT();
  const { convert, symbol, locale } = useCurrency();
  const { plan } = useUserPlan();
  const isPremium = plan === 'premium' || plan === 'vip';

  const initialSym = holdings[0] || 'BTC';
  const [coin, setCoin] = useState(initialSym);
  const [coinId, setCoinId] = useState(COIN_MAP[initialSym]?.id || 'bitcoin');
  const [dir, setDir] = useState('price_above');
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const isHolding = dir === 'holding_period';
  const isPortfolio = dir === 'portfolio_value';
  const needsCoin = !isPortfolio;

  const currentPrice = useMemo(() => {
    if (coinId && marketData?.[coinId]) {
      const m = marketData[coinId];
      return m.price ?? m.current_price ?? null;
    }
    const m = marketData && Object.values(marketData).find(x => (x?.symbol || '').toUpperCase() === coin);
    return m?.price ?? m?.current_price ?? null;
  }, [coin, coinId, marketData]);

  const onPickCondition = (id) => {
    const cond = CONDITIONS.find(c => c.id === id);
    if (cond?.tier === 'premium' && !isPremium) { onOpenPricing?.(); return; }
    setDir(id);
  };

  const valueLabel =
    isHolding ? t('alert_value_warn_days') :
    dir === 'pct_change' ? t('alert_value_percent') :
    isPortfolio ? t('alert_value_portfolio', { sym: symbol }) :
    dir === 'position_value' ? t('alert_value_position', { sym: symbol }) :
    t('alert_value_target_price', { sym: symbol });
  const placeholder = isHolding ? '7' : dir === 'pct_change' ? '5,00' : '0,00';

  const submit = async () => {
    setErr(''); setOk(false);
    const num = parseFloat(String(target).replace(',', '.'));
    if (isHolding) {
      if (!Number.isFinite(num) || num < 1 || num > 365) { setErr(t('alert_err_days_range')); return; }
    } else if (!isFinite(num) || num <= 0) {
      setErr(t('alert_err_invalid_value')); return;
    }
    setBusy(true);
    try {
      const payload = { type: dir, threshold: num, currency: 'EUR', recurring: true };
      if (needsCoin) { payload.coinId = coinId; payload.coinSym = coin; }
      await createAlert(payload);
      setOk(true);
      setTarget('');
      onCreated?.();
      setTimeout(() => setOk(false), 2000);
    } catch (e) {
      if (e instanceof ApiError && e.requiresUpgrade) { onOpenPricing?.(); }
      else if (e instanceof ApiError && e.message) setErr(e.message);
      else setErr(t('alert_err_create_failed'));
    } finally {
      setBusy(false);
    }
  };

  const labelStyle = {
    fontFamily: F.family, fontSize: 12, color: T.sub,
    fontWeight: 600, display: 'block', marginBottom: 8,
  };

  return (
    <div>
      <h3 style={{
        fontFamily: F.display, fontSize: 18, fontWeight: 800,
        letterSpacing: '-0.025em', margin: 0, color: T.text,
      }}>{t('alert_new_alert')}</h3>
      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>

        {/* Bedingung — alle Typen */}
        <div>
          <label style={labelStyle}>{t('alert_condition')}</label>
          <ModernDropdown
            value={dir}
            onChange={onPickCondition}
            options={CONDITIONS.map(c => ({
              id: c.id,
              label: t(c.labelKey, { sym: symbol }),
              badge: c.tier === 'premium' && !isPremium ? <TierBadge tier="premium" scale={0.75} /> : null,
            }))}
            T={T}
          />
        </div>

        {/* Coin — öffnet Such-Overlay (nicht bei Portfolio-Wert) */}
        {needsCoin && (
          <div>
            <label style={labelStyle}>{t('coin')}</label>
            <button
              onClick={() => setPickerOpen(true)}
              style={{
                width: '100%', boxSizing: 'border-box',
                display: 'flex', alignItems: 'center', gap: 10,
                background: T.surfaceHi, border: `1px solid ${T.line}`,
                borderRadius: mr(16), padding: '9px 12px',
                color: T.text, fontFamily: F.family, fontSize: 14,
                fontWeight: 700, cursor: 'pointer', textAlign: 'left',
              }}
            >
              <CoinImage sym={coin} marketData={marketData} size={24} />
              <span style={{ flex: 1 }}>{coin}</span>
              <span style={{ color: T.sub, fontSize: 12 }}>{t('alert_change')} ›</span>
            </button>
          </div>
        )}

        {/* Wert */}
        <div>
          <label style={labelStyle}>{valueLabel}</label>
          <input
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            type="text"
            inputMode="decimal"
            placeholder={placeholder}
            style={{
              width: '100%', boxSizing: 'border-box',
              background: T.surfaceHi, border: `1px solid ${T.line}`,
              borderRadius: mr(16), padding: '12px 14px',
              color: T.text, fontSize: 15, fontFamily: F.num,
              fontWeight: 700, outline: 'none',
              ...MONO_FEAT,
            }}
          />
          {!isHolding && !isPortfolio && dir !== 'pct_change' && needsCoin && currentPrice != null && (
            <div style={{ fontSize: 11.5, color: T.mute, marginTop: 6, fontFamily: F.family }}>
              {t('alert_current')} <span style={{ ...MONO_FEAT }}>{fmt(convert(Number(currentPrice)), locale)} {symbol}</span>
            </div>
          )}
          {isHolding && (
            <div style={{ fontSize: 11.5, color: T.mute, marginTop: 6, fontFamily: F.family, lineHeight: 1.5 }}>
              {t('alert_holding_hint', { coin })}
            </div>
          )}
        </div>

        {err && (<div style={{ fontSize: 12, color: accent.signal, fontFamily: F.family }}>{err}</div>)}
        {ok && (<div style={{ fontSize: 12, color: T.gain || T.green, fontFamily: F.family }}>✓ {t('alert_created_ok')}</div>)}

        <button
          onClick={submit}
          disabled={busy}
          onMouseDown={(e) => { if (!busy) e.currentTarget.style.transform = 'scale(0.97)'; }}
          onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            gap: 9, width: '100%',
            padding: '14px 16px',
            background: busy ? T.surfaceHi : T.text,
            color: busy ? T.sub : T.bg,
            border: 'none',
            borderRadius: 14,
            fontFamily: F.family, fontSize: 14.5, fontWeight: 700,
            letterSpacing: '-0.01em',
            cursor: busy ? 'not-allowed' : 'pointer',
            transition: 'transform .12s',
          }}
        >
          <Icon d={Icons.bell} size={15} stroke={busy ? T.sub : T.bg} sw={2} />
          {busy ? t('alert_creating') : t('alert_create')}
        </button>
      </div>

      <CoinSearchOverlay
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(c) => { setCoin(c.sym); setCoinId(c.id); }}
        marketData={marketData}
        T={T}
      />
    </div>
  );
}

// ── Hauptkomponente ─────────────────────────────────────────────────────────
export default function AlertsScreen({ onBack, onOpenPricing, txs = [], marketData = {}, prefill = null, onPrefillConsumed }) {
  const { t } = useT();
  const { T, isDark } = useTheme();
  const { isMobile } = useBreakpoint();
  const accent = makeAccent(T.signal || T.red);

  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [permission, setPermission] = useState('unknown');
  const [supported, setSupported] = useState(true);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState('');
  // pushReady = es existiert ein registrierter Token (Push wird zugestellt).
  // NICHT identisch zur Browser-Permission: die bleibt nach disablePush auf
  // 'granted' und kann per Code nicht widerrufen werden. Wir steuern den
  // An/Aus-Zustand daher über diesen eigenen Flag, nicht über permission.
  const [pushReady, setPushReady] = useState(false);

  const holdings = useMemo(() => deriveHoldings(txs), [txs]);

  const refresh = async () => {
    setLoading(true);
    try { setAlerts(await listAlerts()); } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => {
    refresh();
    (async () => {
      const s = await isPushSupported();
      setSupported(s);
      const perm = s ? getPermissionState() : 'unsupported';
      setPermission(perm);
      // Selbstheilung: wenn die Erlaubnis schon erteilt ist, aber (z.B. wegen
      // eines früheren Builds ohne VAPID-Key) nie ein Token gespeichert wurde,
      // holen wir ihn jetzt still nach. Sonst bliebe sent:0, obwohl der Button
      // "aktiv" anzeigt.
      if (perm === 'granted') {
        const ok = await ensurePushRegistered();
        setPushReady(ok);
      }
    })();
  }, []);

  useEffect(() => {
    if (prefill) setWizardOpen(true);
  }, [prefill]);

  const handleToggle = async (a) => {
    const next = a.active === false; // neuer Aktiv-Zustand
    // Optimistisch sofort umlegen — der Switch reagiert ohne auf das Netz zu
    // warten. Erst danach den Server informieren; bei Fehler zurückrollen.
    setAlerts(prev => prev.map(x => x.id === a.id ? { ...x, active: next } : x));
    try {
      await updateAlert(a.id, { active: next });
    } catch {
      setAlerts(prev => prev.map(x => x.id === a.id ? { ...x, active: !next } : x));
    }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(t('alert_confirm_delete'))) return;
    try {
      await deleteAlert(a.id);
      setAlerts(prev => prev.filter(x => x.id !== a.id));
    } catch { /* ignore */ }
  };

  const handleDisablePush = async () => {
    if (!window.confirm(t('alert_confirm_disable_push'))) return;
    setPushBusy(true); setPushError('');
    try {
      await disablePush();
      setPushReady(false); // Umschalter umlegen — Permission bleibt 'granted'.
      setPermission(getPermissionState());
    } finally {
      setPushBusy(false);
    }
  };

  const handleEnablePush = async () => {
    setPushBusy(true); setPushError('');
    try {
      // Erlaubnis schon erteilt → kein Dialog nötig, nur Token (neu) registrieren.
      // Sonst voller Flow (Permission-Dialog + Token).
      if (getPermissionState() === 'granted') {
        // silent=false → echte Ursache wird geworfen und unten angezeigt.
        await ensurePushRegistered(false);
      } else {
        await enablePush();
      }
      setPushReady(true);
      setPermission(getPermissionState());
    } catch (e) {
      // Fehler sichtbar machen statt still schlucken — sonst wirkt der Button
      // wie "kaputt".
      setPushError(e?.message || t('alert_push_enable_failed'));
    } finally {
      setPushBusy(false);
    }
  };

  const list = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {loading ? (
        <div style={{ textAlign: 'center', color: T.muted, padding: 20, fontSize: 13, fontFamily: F.family }}>
          {t('loading')}
        </div>
      ) : alerts.length === 0 ? (
        <div style={{
          padding: '64px 24px', textAlign: 'center',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
        }}>
          {/* Minimal: Bell + roter Akzent-Punkt (Notification-Metapher) */}
          <div style={{ position: 'relative', display: 'inline-flex' }} aria-hidden="true">
            <Icon d={Icons.bell} size={36} stroke={T.mute} sw={1.5} />
            <span style={{
              position: 'absolute', top: 2, right: 1,
              width: 9, height: 9, borderRadius: 999,
              background: accent.signal, border: `2px solid ${T.bg}`,
            }} />
          </div>
          <div style={{ maxWidth: 300 }}>
            <div style={{ fontFamily: F.display, fontSize: 17, fontWeight: 700, letterSpacing: '-0.02em', color: T.text, marginBottom: 6 }}>
              {t('alert_empty_title')}
            </div>
            <div style={{ fontSize: 12.5, color: T.sub, fontFamily: F.family, lineHeight: 1.5 }}>
              {isMobile ? t('alert_empty_hint_below') : t('alert_empty_hint_right')} ›
            </div>
          </div>
        </div>
      ) : (
        alerts.map(a => (
          <AlertRow key={a.id}
            a={a} marketData={marketData}
            onToggle={handleToggle} onDelete={handleDelete}
            T={T} accent={accent}
          />
        ))
      )}

      {/* Push-Settings — echter Umschalter anhand pushReady (Token registriert),
          nicht anhand der Browser-Permission (die bleibt nach Deaktivieren
          'granted' und liesse sich sonst nie wieder umschalten). */}
      {supported && permission !== 'denied' && !(isIOSSafari() && !isStandalonePWA()) && (
        pushReady ? (
          <button onClick={handleDisablePush} disabled={pushBusy} style={{
            marginTop: 14,
            width: '100%', padding: '13px', background: 'none',
            color: T.sub, border: `1px solid ${T.line}`,
            borderRadius: 14, fontFamily: F.family, fontSize: 13.5,
            fontWeight: 600, cursor: pushBusy ? 'not-allowed' : 'pointer',
          }}>{pushBusy ? t('alert_one_moment') : t('alert_push_disable')}</button>
        ) : (
          <button onClick={handleEnablePush} disabled={pushBusy} style={{
            marginTop: 14,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 9,
            width: '100%', padding: '13px',
            background: T.surface,
            color: pushBusy ? T.mute : T.text,
            border: `1px solid ${T.line}`,
            boxShadow: isDark ? '0 8px 22px rgba(0,0,0,0.30)' : '0 8px 22px rgba(34,30,24,0.12)',
            borderRadius: 14, fontFamily: F.family, fontSize: 13.5,
            fontWeight: 600, cursor: pushBusy ? 'not-allowed' : 'pointer',
          }}>
            {!pushBusy && <Icon d={Icons.bell} size={16} stroke={T.sub} sw={1.8} />}
            {pushBusy ? t('alert_activating') : t('alert_push_enable')}
          </button>
        )
      )}
      {pushError && (
        <div style={{
          marginTop: 8, fontSize: 12, color: accent.signal,
          fontFamily: F.family, lineHeight: 1.5,
          whiteSpace: 'pre-line',
        }}>{pushError}</div>
      )}
    </div>
  );

  const createCard = (
    <CreateCard
      holdings={holdings}
      marketData={marketData}
      onCreated={refresh}
      onOpenPricing={onOpenPricing}
      T={T} accent={accent}
    />
  );

  return (
    <div style={{ padding: '20px 16px 100px', color: T.text, maxWidth: 1280, margin: '0 auto' }}>
      {/* Title-Block — Calm: ruhiger Sub, Display-Headline, Hairline (kein
          roter Caps-Kicker, kein 2px-Text-Streifen). */}
      <div style={{ marginBottom: 26 }}>
        <div style={{
          fontSize: 13, color: T.sub, fontWeight: 500,
          letterSpacing: '-0.005em', marginBottom: 6, fontFamily: F.family,
        }}>{t('alert_eyebrow')}</div>
        <h1 style={{
          fontFamily: F.display,
          fontSize: 'clamp(26px, 3vw, 38px)',
          fontWeight: 800,
          letterSpacing: '-0.04em',
          lineHeight: 1.05,
          margin: 0,
          color: T.text,
        }}>{t('alert_title_heading')}</h1>
        <div style={{
          marginTop: 14, fontSize: 14, color: T.sub,
          maxWidth: 620, lineHeight: 1.55, fontFamily: F.family,
        }}>
          {t('alert_subtitle')}
        </div>
        <div style={{ height: 1, background: T.line, marginTop: 22 }} />
      </div>

      {/* PWA / Permission banners */}
      {!supported && (
        <div style={{ marginBottom: 14 }}>
          <InfoBanner tone="warning" title={t('alert_banner_unsupported_title')}>
            {t('alert_banner_unsupported_body')}
          </InfoBanner>
        </div>
      )}
      {supported && permission === 'denied' && (
        <div style={{ marginBottom: 14 }}>
          <InfoBanner tone="warning" title={t('alert_banner_blocked_title')}>
            {t('alert_banner_blocked_body')}
          </InfoBanner>
        </div>
      )}
      {supported && permission === 'default' && isIOSSafari() && !isStandalonePWA() && (
        <div style={{ marginBottom: 14 }}>
          <InfoBanner tone="info" title={t('alert_banner_ios_title')}>
            {t('alert_banner_ios_body')}
          </InfoBanner>
        </div>
      )}

      {/* 2-col grid (desktop) / stacked (mobile) */}
      {isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {list}
          {createCard}
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) 1px 380px',
          columnGap: 30,
          alignItems: 'stretch',
        }}>
          {list}
          <div style={{ background: T.line }} />
          <div style={{ position: 'sticky', top: 88, alignSelf: 'start' }}>{createCard}</div>
        </div>
      )}

      <AlertWizard
        open={wizardOpen}
        onClose={() => { setWizardOpen(false); onPrefillConsumed?.(); }}
        onCreated={() => refresh()}
        onOpenPricing={onOpenPricing}
        txs={txs}
        marketData={marketData}
        prefill={prefill}
      />
    </div>
  );
}
